import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';
import styled from 'styled-components';

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 80vh;
  text-align: center;
`;

const Message = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
`;

const StyledLink = styled(Link)`
  padding: 10px 20px;
  background-color: #005eb8;
  color: white;
  text-decoration: none;
  border-radius: 5px;
  font-weight: bold;
`;

const PaymentSuccess = () => {
    const [status, setStatus] = useState('processing');
    const [errorMessage, setErrorMessage] = useState('');
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const customerEmail = sessionStorage.getItem('customerEmailForSuccess');
    const confirmationSent = useRef(false); // Prevent duplicate calls in React StrictMode

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const paymentIntentId = searchParams.get('payment_intent');

        if (paymentIntentId) {
            if (confirmationSent.current) {
                return;
            }
            confirmationSent.current = true;

            fetch(`${process.env.REACT_APP_BACKEND_URL}/confirm-payment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    payment_intent_id: paymentIntentId,
                    email: customerEmail, // Pass the email as a fallback
                }),
            })
                .then(async (res) => {
                    if (!res.ok) {
                        const errData = await res.json().catch(() => ({ error: 'An unknown error occurred during payment confirmation.' }));
                        throw new Error(errData.error);
                    }
                    return res.json();
                })
                .then(data => {
                    if (data.success) {
                        setStatus('success');

                    // Handle newsletter subscription if they ticked the box at checkout
                    const agreedToMarketing = sessionStorage.getItem('agreedToMarketing') === 'true';
                    if (agreedToMarketing && customerEmail) {
                        fetch(`${process.env.REACT_APP_BACKEND_URL}/newsletter/subscribe`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ email: customerEmail }),
                        })
                            .then(res => res.json())
                            .then(subData => {
                                if (subData.success) {
                                    toast.info(subData.message);
                                }
                            })
                            .catch(err => console.error('Newsletter error:', err));
                    }

                        if (isAuthenticated) {
                            // Automatically redirect to account page after 2 seconds if already logged in
                            setTimeout(() => navigate('/account'), 2000);
                        }
                    } else {
                        throw new Error(data.error || 'Payment confirmation failed.');
                    }
                })
                .catch((err) => {
                    setStatus('error');
                    setErrorMessage(err.message);
                })
                .finally(() => {
                    // Clean up the email from session storage
                    sessionStorage.removeItem('customerEmailForSuccess');
                    sessionStorage.removeItem('agreedToMarketing');
                });
        } else {
            setStatus('error');
            setErrorMessage('Payment details not found in URL.');
        }
    }, [location, customerEmail, isAuthenticated, navigate]);

    return (
        <SuccessContainer>
            {status === 'processing' && <Message>Verifying your payment...</Message>}
            {status === 'success' && (
                <>
                    <Message>
                        ✅ Payment successful! {isAuthenticated ? 'Redirecting to your orders...' : 'Your account is ready. Please log in to upload your documents.'}
                    </Message>
                    <StyledLink to={isAuthenticated ? "/account" : "/login"}>
                        {isAuthenticated ? "Go to My Account" : "Go to Login"}
                    </StyledLink>
                </>
            )}
            {status === 'error' && (
                <>
                    <Message>❌ There was an error processing your payment. {errorMessage}</Message>
                    <StyledLink to="/checkout">Try Again</StyledLink>
                </>
            )}
        </SuccessContainer>
    );
};

export default PaymentSuccess;